import { Link } from "react-router-dom";
import { Instagram, Facebook, MessageCircle } from "lucide-react";
import { useSiteContent } from "@/context/SiteContentContext";
import { useAuth } from "@/context/AuthContext";
import MenuLink from "@/components/MenuLink";
import { MENU_KEYS, DEFAULT_SHOP_LINKS, parseLinks } from "@/lib/menuLinks";

export default function Footer() {
  const { t, content } = useSiteContent();
  const { user } = useAuth();
  const isVendorOrAdmin = user && (user.role === "vendor" || user.role === "admin");
  const loaded = Object.keys(content).length > 0;
  const shopLinks = parseLinks(content[MENU_KEYS.shopLinks], DEFAULT_SHOP_LINKS);
  const showShop = loaded && content[MENU_KEYS.shopVisible] !== "false" && shopLinks.length > 0;
  return (
    <footer className="mt-24 border-t border-gray-200 bg-white" data-testid="site-footer">
      <div className={`mx-auto max-w-7xl px-4 lg:px-8 py-12 grid gap-10 ${showShop ? "md:grid-cols-5" : "md:grid-cols-4"}`}>
        <div>
          <div className="flex items-center gap-2 mb-3">
            <img src="/cityshor-logo.png" alt="CityShor" className="h-10 w-10 rounded-full object-cover" />
            <div className="font-display text-xl">CityShor</div>
          </div>
          <p className="text-sm text-gray-600">{t("footer.tagline", "India's Online Lifestyle Exhibition Platform. Where offline exhibitors meet online buyers.")}</p>
          <div className="flex gap-3 mt-4">
            <a href={t("social.instagram", "https://instagram.com")} target="_blank" rel="noreferrer" data-testid="social-instagram" className="p-2 rounded-full hover:bg-gray-100"><Instagram className="h-4 w-4" /></a>
            <a href={t("social.facebook", "https://facebook.com")} target="_blank" rel="noreferrer" data-testid="social-facebook" className="p-2 rounded-full hover:bg-gray-100"><Facebook className="h-4 w-4" /></a>
            <a href={(() => { const v = (t("footer.support_whatsapp", "") || t("contact.whatsapp", "")).trim(); if (/^https?:\/\//i.test(v)) return v; const d = v.replace(/\D/g, ""); return d ? `https://wa.me/${d}` : "https://wa.me/917990391645"; })()} target="_blank" rel="noreferrer" data-testid="social-whatsapp" className="p-2 rounded-full hover:bg-gray-100"><MessageCircle className="h-4 w-4" /></a>
          </div>
        </div>
        {showShop && (
        <div data-testid="footer-shop-section">
          <div className="text-xs font-bold uppercase tracking-[0.2em] mb-3 text-gray-900">Shop</div>
          <ul className="space-y-2 text-sm text-gray-600">
            {shopLinks.map((l, i) => (
              <li key={`${l.url}-${i}`}><MenuLink url={l.url} data-testid={`footer-shop-link-${i}`}>{l.name}</MenuLink></li>
            ))}
          </ul>
        </div>
        )}
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] mb-3 text-gray-900">Company</div>
          <ul className="space-y-2 text-sm text-gray-600">
            <li><Link to="/about" data-testid="footer-about">About / Vision &amp; Mission</Link></li>
            <li><Link to="/about#team">Team &amp; Founders</Link></li>
            <li><Link to="/vendor-register" data-testid="footer-become-vendor">Become an Online Exhibitor</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] mb-3 text-gray-900">Policies</div>
          <ul className="space-y-2 text-sm text-gray-600">
            <li><Link to="/legal/buyer-terms" data-testid="footer-buyer-terms">Buyer Terms &amp; Conditions</Link></li>
            <li><Link to="/legal/privacy" data-testid="footer-privacy">Privacy Policy</Link></li>
            <li><Link to="/legal/shipping-delivery" data-testid="footer-shipping-delivery">Shipping &amp; Delivery Policy</Link></li>
            <li><Link to="/legal/returns-refunds" data-testid="footer-returns-refunds">Returns &amp; Refunds Policy</Link></li>
            <li><Link to="/legal/grievance" data-testid="footer-grievance">Grievance / Complaints</Link></li>
            {isVendorOrAdmin && (
              <>
                <li><Link to="/legal/vendor-agreement" data-testid="footer-vendor-agreement">Vendor Agreement</Link></li>
                <li><Link to="/legal/vendor-payout-policy" data-testid="footer-vendor-payout">Vendor Payout Policy</Link></li>
              </>
            )}
          </ul>
        </div>
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] mb-3 text-gray-900">Support</div>
          <ul className="space-y-2 text-sm text-gray-600">
            <li data-testid="footer-support-whatsapp">{t("footer.support_whatsapp", "WhatsApp: +91-7069225050")}</li>
            <li data-testid="footer-support-email">{t("footer.support_email", "Email: popupbazaar@cityshor.com")}</li>
            <li data-testid="footer-support-note">{t("footer.support_note", "Shipping charges extra citywise")}</li>
          </ul>
        </div>
      </div>
      <div className="border-t border-gray-200 py-4 text-center text-xs text-gray-500" data-testid="footer-copyright">
        © {new Date().getFullYear()} {t("footer.copyright", "CityShor Pop-Up Bazaar. Made with love in India.")}
      </div>
    </footer>
  );
}
